import { useState } from "react";
import { InfoIcon } from "./icons";
import type { AuthUser } from "../types/auth";

interface AccountPanelProps {
  user: AuthUser;
  onAuthChange: (user: AuthUser | null) => void;
}

export default function AccountPanel({
  user,
  onAuthChange,
}: AccountPanelProps) {
  const [barkKeyInput, setBarkKeyInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const persistBarkKey = async (nextBarkKey: string) => {
    setSubmitting(true);
    setMessage("");
    try {
      const res = await fetch("/api/account/bark-key", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bark_key: nextBarkKey.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data.error || "保存 Bark Key 失败");
        return;
      }
      onAuthChange(data.user || null);
      setBarkKeyInput("");
      setMessage(data.user?.has_bark_key ? "Bark Key 已保存" : "Bark Key 已清除");
    } catch (error) {
      console.error("Save bark key failed:", error);
      setMessage("保存 Bark Key 失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setSubmitting(true);
    setMessage("");
    try {
      await fetch("/api/auth/logout", { method: "POST" });
      onAuthChange(null);
      setBarkKeyInput("");
      setMessage("已退出登录");
    } catch (error) {
      console.error("Logout failed:", error);
      setMessage("退出失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-lg">🔐</span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-800">账号与 Bark Key</h4>
            <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
              已登录
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {`${user.email} · ${user.has_bark_key ? `Bark Key: ${user.bark_key_masked}` : "未配置 Bark Key"}`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          disabled={submitting}
          className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50"
        >
          退出
        </button>
      </div>

      <div className="mt-3 space-y-3">
        <div className="flex gap-2">
          <input
            type="password"
            value={barkKeyInput}
            onChange={(event) => setBarkKeyInput(event.target.value)}
            placeholder={user.has_bark_key ? "输入新的 Bark Key，留空可清除" : "输入 Bark Key"}
            className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
          />
          <button
            type="button"
            onClick={() => void persistBarkKey(barkKeyInput)}
            disabled={submitting}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-blue-600 transition-colors"
          >
            保存
          </button>
        </div>
        {user.has_bark_key && (
          <button
            type="button"
            onClick={() => void persistBarkKey("")}
            disabled={submitting}
            className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50"
          >
            清除 Bark Key
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowHelp(!showHelp)}
        className="mt-3 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        <InfoIcon className="w-3 h-3" />
        {showHelp ? "收起说明" : "为什么这样更安全？"}
      </button>

      {showHelp && (
        <div className="mt-3 p-3 bg-white rounded-lg text-xs text-gray-600 space-y-2">
          <p>1. 浏览器不再把 Bark Key 明文保存在 localStorage。</p>
          <p>2. Bark Key 只在服务端加密存储，页面上只显示脱敏结果。</p>
          <p>3. 通知订阅与历史现在跟登录账号绑定，不再依赖 URL 里的 bark_key 参数。</p>
        </div>
      )}

      {message && (
        <div className="mt-3 text-xs text-gray-600 bg-white/80 rounded-lg px-3 py-2">
          {message}
        </div>
      )}
    </div>
  );
}
